/**
 * Helper functions for extracting display values from order book quotes.
 * These functions are side-agnostic and determine which value format to return
 * based on the showPreAndPostFeeValues flag and presence of enriched fee data.
 */

import type {
  PriceDisplayValue,
  PriceQuoteInput,
  SpreadDisplayValue,
  SpreadQuoteInput,
  YieldDisplayValue,
  YieldQuoteInput,
} from './types'

// Spread helpers
export function hasPreFeeSpreadValue(
  quote: SpreadQuoteInput,
): quote is SpreadQuoteInput & { preFeeSpreadBps: number } {
  return 'preFeeSpreadBps' in quote && !!quote.preFeeSpreadBps
}

export function hasPostFeeSpreadValue(
  quote: SpreadQuoteInput,
): quote is SpreadQuoteInput & { postFeeSpreadBps: number } {
  return 'postFeeSpreadBps' in quote && !!quote.postFeeSpreadBps
}

// Price helpers
export function hasPreFeePriceValue(
  quote: PriceQuoteInput,
): quote is PriceQuoteInput & { preFeePrice: number } {
  return 'preFeePrice' in quote && !!quote.preFeePrice
}

export function hasPostFeePriceValue(
  quote: PriceQuoteInput,
): quote is PriceQuoteInput & { postFeePrice: number } {
  return 'postFeePrice' in quote && !!quote.postFeePrice
}

// Yield helpers
export function hasPreFeeYieldValue(
  quote: YieldQuoteInput,
): quote is YieldQuoteInput & { preFeeYtw: number } {
  return 'preFeeYtw' in quote && !!quote.preFeeYtw
}

export function hasPostFeeYieldValue(
  quote: YieldQuoteInput,
): quote is YieldQuoteInput & { postFeeYtw: number } {
  return 'postFeeYtw' in quote && !!quote.postFeeYtw
}

export function hasEnrichedSpreadValues(quote: SpreadQuoteInput): boolean {
  return hasPreFeeSpreadValue(quote) || hasPostFeeSpreadValue(quote)
}

export function hasEnrichedPriceValues(quote: PriceQuoteInput): boolean {
  return hasPreFeePriceValue(quote) || hasPostFeePriceValue(quote)
}

export function hasEnrichedYieldValues(quote: YieldQuoteInput): boolean {
  return hasPreFeeYieldValue(quote) || hasPostFeeYieldValue(quote)
}

/**
 * Gets the display spread value from a quote.
 *
 * @param quote - The order book quote
 * @param showPreAndPostFeeValues - Whether to show pre and post fee values
 * @returns Object with pre/post fee values if enriched and flag is true, otherwise the base spread value or null
 */
export function getDisplaySpreadValue(
  quote: SpreadQuoteInput,
  showPreAndPostFeeValues: boolean,
): SpreadDisplayValue {
  if (showPreAndPostFeeValues && hasEnrichedSpreadValues(quote)) {
    return {
      preFeeSpreadBps: quote.preFeeSpreadBps,
      postFeeSpreadBps: quote.postFeeSpreadBps,
    }
  }

  if (hasPreFeeSpreadValue(quote)) {
    return quote.preFeeSpreadBps
  }

  return quote.spread_to_benchmark_bps
}

/**
 * Gets the display price value from a quote.
 *
 * @param quote - The order book quote
 * @param showPreAndPostFeeValues - Whether to show pre and post fee values
 * @returns Object with pre/post fee values if enriched and flag is true, otherwise the base price value or null
 */
export function getDisplayPriceValue(
  quote: PriceQuoteInput,
  showPreAndPostFeeValues: boolean,
): PriceDisplayValue {
  if (showPreAndPostFeeValues && hasEnrichedPriceValues(quote)) {
    return {
      preFeePrice: quote.preFeePrice,
      postFeePrice: quote.postFeePrice,
    }
  }

  if (hasPreFeePriceValue(quote)) {
    return quote.preFeePrice
  }

  return quote.price
}

/**
 * Gets the display yield value from a quote.
 *
 * @param quote - The order book quote
 * @param showPreAndPostFeeValues - Whether to show pre and post fee values
 * @returns Object with pre/post fee values if enriched and flag is true, otherwise the base yield value or null
 */
export function getDisplayYieldValue(
  quote: YieldQuoteInput,
  showPreAndPostFeeValues: boolean,
): YieldDisplayValue {
  if (showPreAndPostFeeValues && hasEnrichedYieldValues(quote)) {
    return {
      preFeeYtw: quote.preFeeYtw,
      postFeeYtw: quote.postFeeYtw,
    }
  }

  if (hasPreFeeYieldValue(quote)) {
    return quote.preFeeYtw
  }

  return quote.yield_to_worst
}
